import {
  ONE_RM_PROTOCOL_STEPS,
  ONE_RM_REFERENCE_NOTES,
  formatOneRMKg,
  normalizeOneRMPayload,
  summarizeOneRMPayload,
  type OneRMResultPayload,
} from '../one-rm/oneRmDomain';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export const getOneRMSeverityLabel = (payload: OneRMResultPayload | null | undefined): string => {
  const summary = summarizeOneRMPayload(payload);
  return summary.groupCount > 0 ? 'Registro concluído' : 'Sem dados suficientes';
};

export const getOneRMSeverityTone = (payload: OneRMResultPayload | null | undefined): string => {
  const summary = summarizeOneRMPayload(payload);
  return summary.groupCount > 0 ? 'text-emerald-700' : 'text-amber-700';
};

export const getOneRMInterpretationText = (payload: OneRMResultPayload | null | undefined): string => {
  const normalized = normalizeOneRMPayload(payload);
  const summary = summarizeOneRMPayload(payload);
  if (!normalized || summary.groupCount === 0) {
    return 'Não há grupos musculares válidos registrados para interpretar o teste de 1RM.';
  }

  const bestEntry = normalized.entries.find((entry) => entry.muscleGroup === summary.bestGroupLabel);
  const bestMethod = bestEntry?.direct1RMKg != null ? 'medido diretamente' : 'estimado por carga submáxima';
  const bestLabel = summary.bestOverallKg != null && summary.bestGroupLabel
    ? `O maior resultado foi ${formatOneRMKg(summary.bestOverallKg)} em ${summary.bestGroupLabel}, ${bestMethod}.`
    : '';

  const methodLabel =
    summary.directCount > 0 && summary.estimatedCount > 0
      ? 'Foram utilizados resultados diretos e estimados por carga submáxima.'
      : summary.directCount > 0
        ? 'Os resultados foram obtidos por medida direta de 1RM.'
        : 'Os resultados foram estimados a partir de carga submáxima.';

  return resolveAssessmentInterpretationText(
    payload,
    `Foram avaliados ${summary.groupCount} grupo(s) muscular(es). ${bestLabel} ${methodLabel} As estimativas correspondem à média das equações de Mayhew e Wathen.`,
  );
};

interface OneRMResultRendererProps {
  payload: OneRMResultPayload | null | undefined;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export default function OneRMResultRenderer({ payload }: OneRMResultRendererProps) {
  const normalized = normalizeOneRMPayload(payload);
  const summary = summarizeOneRMPayload(payload);

  if (!normalized) {
    return (
      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-sm text-slate-500">Dados do 1RM indisponíveis para este resultado.</div>
      </div>
    );
  }

  const severityLabel = getOneRMSeverityLabel(normalized);
  const severityTone = getOneRMSeverityTone(normalized);
  const interpretation = getOneRMInterpretationText(normalized);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">1RM</p>
          <div className="text-6xl font-black leading-none text-primary-800">{summary.groupCount}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {summary.groupCount === 1 ? 'grupo muscular registrado' : 'grupos musculares registrados'}
          </div>
          <div className={`mt-4 inline-flex items-center gap-2 text-xs font-bold ${severityTone}`}>
            <span className={`h-2 w-2 rounded-full ${summary.groupCount > 0 ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {severityLabel}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Resumo do teste</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>

        </div>
      </div>

      <div className="space-y-4">
        {normalized.entries.map((entry) => (
          <div key={entry.id} className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Grupo muscular</div>
                <h5 className="mt-2 text-xl font-black text-slate-900">{entry.muscleGroup}</h5>
              </div>
              {entry.notes ? (
                <div className="max-w-xl rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {entry.notes}
                </div>
              ) : null}
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryMetric label="1RM direto" value={formatOneRMKg(entry.direct1RMKg)} />
              <SummaryMetric
                label="Carga submáxima"
                value={formatOneRMKg(entry.submaxLoadKg)}
                note={entry.repetitions != null ? `${entry.repetitions} repetições` : '—'}
              />
              <SummaryMetric label="Mayhew" value={formatOneRMKg(entry.estimated1RMMayhewKg)} />
              <SummaryMetric label="Wathen" value={formatOneRMKg(entry.estimated1RMWathenKg)} />
              <SummaryMetric label="Estimativa final" value={formatOneRMKg(entry.estimated1RMKg)} note="Média entre equações" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Execução sugerida</div>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            {(normalized.protocolSteps ?? ONE_RM_PROTOCOL_STEPS).map((step) => (
              <div key={step} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                {step}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Equações de referência</div>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            {(normalized.referenceNotes ?? ONE_RM_REFERENCE_NOTES).map((note) => (
              <div key={note} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                {note}
              </div>
            ))}
          </div>
        </div>
      </div>

      {normalized.notes ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Observações gerais</div>
          <div className="mt-4 text-sm leading-relaxed text-slate-700">{normalized.notes}</div>
        </div>
      ) : null}
    </div>
  );
}
